import type {
	Farm as OldFarm,
	SimplePoint,
} from "@ptx-autonomous/libs/api-models";
import {
	BoundaryType,
	LineFeatureType,
	type Api,
	type Boundary,
	type BoundaryUpdate,
	type Field,
	type HttpResponse,
	type LineFeature,
	type NewBoundary,
} from "@ptx-autonomous/libs/flightpath-service-api-models";
import { Speed } from "@ptx-autonomous/libs/units";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import area from "@turf/area";
import type {
	Feature,
	LineString,
	MultiLineString,
	MultiPolygon,
	Polygon,
	Position,
} from "geojson";
import { computed, type Ref } from "vue";
import type { FieldWithBoundary } from "../types";
import { isZoneBoundaryType, toDisplayZoneBoundary } from "../utils/zones";
import { useCustomerIdRef } from "@ptx-autonomous/libs/api";
import { WEB_DEMO_ORG_ID } from "../state";

export function useFarmListQuery(
	http: Api<unknown>,
	allowNonRtkRecordedBoundaries: Ref<boolean>,
) {
	const customerId = useCustomerIdRef();

	return useQuery({
		queryKey: ["farms"],
		staleTime: 1000 * 60 * 5, // Only refetch every 5 minutes (invalidate queries resets this)
		queryFn: async () => {
			const farms = (await http.farms.farmsList()).data;
			// If on web demo org, allow non-RTK recorded boundaries
			const isOnWebDemoOrg = customerId.value === WEB_DEMO_ORG_ID;

			const farmsWithBoundaries = await Promise.all(
				farms.map(async farm => {
					let boundaries: Boundary[];
					try {
						boundaries = (
							await http.farms.boundariesList(farm.id, {
								isRtkRecorded:
									!allowNonRtkRecordedBoundaries.value &&
									!isOnWebDemoOrg,
								boundaryTypes: [
									BoundaryType.Field,
									BoundaryType.InnerField,
									BoundaryType.SpeedZone,
									BoundaryType.NoCoverageZone,
								],
							})
						).data;
					} catch {
						return undefined;
					}

					const fieldBoundary = boundaries.find(
						b => b.type === BoundaryType.Field,
					);
					const zones = boundaries
						.filter(
							(
								boundary,
							): boundary is Boundary & {
								geometry: MultiPolygon;
							} => isZoneBoundaryType(boundary.type),
						)
						.map(zone => toDisplayZoneBoundary(zone));
					// Multipolygon boundaries are not supposed to be used with tillage. However these will be available in the Outrun DB.
					// Allow MultiPolygons with a single polygon by converting them to a Polygon.
					if (fieldBoundary?.geometry.type === "MultiPolygon") {
						if (fieldBoundary.geometry.coordinates?.length !== 1) {
							return undefined;
						}
						return Object.assign(farm, {
							boundary: {
								...fieldBoundary,
								geometry: {
									type: "Polygon",
									coordinates:
										fieldBoundary.geometry.coordinates[0],
								},
							},
							zones,
						}) as FieldWithBoundary;
					}
					return Object.assign(farm, {
						boundary: fieldBoundary,
						innerBoundary: boundaries.find(
							b => b.type === BoundaryType.InnerField,
						),
						zones,
					}) as FieldWithBoundary;
				}),
			);
			return farmsWithBoundaries.filter(
				(farm): farm is FieldWithBoundary => !!farm,
			);
		},
	});
}

export function useFarmCreateMutation(http: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (farm: { name: string }) => {
			const response = await http.farms.farmsCreate({
				name: farm.name,
			});
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["farms"] });
		},
	});
}

export function useBoundaryCreateMutation(http: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (options: {
			farmId: string;
			boundary: Polygon;
			innerBoundary?: Polygon;
		}) => {
			const response = await http.farms.boundariesCreate(options.farmId, {
				geometry: {
					type: "Polygon",
					coordinates: options.boundary.coordinates,
				},
				type: BoundaryType.Field,
				innerField: options.innerBoundary
					? {
							type: "Polygon",
							coordinates: options.innerBoundary.coordinates,
						}
					: undefined,
				IsRtkRecorded: true,
			});
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["farms"] });
		},
	});
}

export function useFarmAndBoundaryCreateMutation(http: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (farm: {
			name: string;
			boundaries: MultiPolygon | Polygon;
		}) => {
			let response: HttpResponse<string, void> | undefined = undefined;
			// Note: Filter out small polygon slivers created from inflections in drawing
			const MIN_AREA_M2 = 100;

			if (farm.boundaries.type === "MultiPolygon") {
				// Filter out polygons smaller than MIN_AREA_M2
				const validPolygons = farm.boundaries.coordinates.filter(
					coords => {
						const polygonArea = area({
							type: "Polygon",
							coordinates: coords,
						});
						return polygonArea >= MIN_AREA_M2;
					},
				);

				if (validPolygons.length === 0) {
					throw new Error(
						"No valid field boundaries found. All polygons are too small.",
					);
				}

				await Promise.all(
					validPolygons.map(async (boundary, index) => {
						response = await http.farms.farmsCreate({
							name:
								validPolygons.length === 1
									? farm.name
									: `${farm.name} #${index + 1}`,
						});
						await http.farms.boundariesCreate(response.data, {
							geometry: {
								type: "Polygon",
								coordinates: boundary,
							},
							type: BoundaryType.Field,
						});
					}),
				);
			} else {
				// Check if single polygon is valid
				const polygonArea = area(farm.boundaries);
				if (polygonArea < MIN_AREA_M2) {
					throw new Error(
						"Field boundary is too small. Please draw a larger area.",
					);
				}

				response = await http.farms.farmsCreate({
					name: farm.name,
				});

				const geometry = {
					geometry: {
						type: "Polygon",
						coordinates: farm.boundaries.coordinates.map(coord => {
							return coord.map(point => [point[0], point[1]]);
						}),
					},
					type: BoundaryType.Field,
				} as NewBoundary;
				await http.farms.boundariesCreate(response.data, geometry);
			}
			return response!.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["farms"] });
		},
	});
}

export function useFarmDeleteMutation(http: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (farmId: string) => {
			await http.farms.farmsDelete(farmId, { deleteBoundaries: true });
		},
		onSuccess: (_, params) => {
			const queryData: Field[] | undefined = queryClient.getQueryData([
				"farms",
			]);
			if (!queryData) {
				queryClient.invalidateQueries({ queryKey: ["farms"] });
			} else {
				queryClient.setQueryData(
					["farms"],
					queryData?.filter(farm => farm.id !== params),
				);
			}
		},
	});
}

export function fromSimplePoints(
	points?: SimplePoint[],
): Position[] | undefined {
	if (!points) return;
	const geoJsonPoints = points.map(point => [point.Long, point.Lat]);
	geoJsonPoints.push(geoJsonPoints[0]);
	return geoJsonPoints;
}

export function toSimplePoints(geoJson?: Position[]) {
	if (!geoJson) return [];
	const points: SimplePoint[] = geoJson.map(
		point =>
			({
				Lat: point[1],
				Long: point[0],
				Alt: 0,
			}) as SimplePoint,
	);
	if (
		points.length > 1 &&
		points[0].Long === points[points.length - 1].Long &&
		points[0].Lat === points[points.length - 1].Lat
	) {
		points.pop();
	}

	return points;
}

export type FarmFeature = Feature<
	Polygon,
	{
		name: string;
		farmId: string;
	}
>;

export function farmToGeoJson(farm?: OldFarm): FarmFeature | undefined {
	if (!farm) return;
	const points =
		farm.FarmBoundary?.map(point => [point.Long, point.Lat]) ?? [];
	if (points.length === 0 || !farm.Name || !farm.FarmId) return;
	points.push(points[0]);
	return {
		type: "Feature",
		geometry: {
			type: "Polygon",
			coordinates: [points],
		},
		properties: {
			name: farm.Name,
			farmId: farm.FarmId,
		},
	};
}

export function useCreateZoneMutation(http: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (params: {
			farmId: string;
			zonePolygon: MultiPolygon;
			zoneType: BoundaryType.NoCoverageZone | BoundaryType.SpeedZone;
			speedLimit: number;
			name?: string;
		}) => {
			return (
				await http.farms.boundariesCreate(params.farmId, {
					geometry: {
						type: "MultiPolygon",
						coordinates: params.zonePolygon.coordinates,
					},
					type: params.zoneType,
					SpeedLimit: new Speed(params.speedLimit, "mph").convert(
						"m/s",
					),
					name: params.name?.trim() || undefined,
				})
			).data;
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["farms"],
			});
		},
	});
}

export function useDeleteZoneMutation(http: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (params: { farmId: string; boundaryId: string }) => {
			return await http.boundaries.boundariesDelete(params.boundaryId);
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["farms"],
			});
		},
	});
}

export function useGuidanceLinesQuery(
	http: Api<unknown>,
	farmId: Ref<string | undefined>,
) {
	return useQuery({
		queryKey: ["guidance-lines", farmId],
		enabled: computed(() => !!farmId.value),
		queryFn: async () => {
			const result = await http.farms.linefeaturesList(farmId.value!, {
				lineFeatureTypes: [LineFeatureType.GuidanceLine],
			});
			return result.data.filter(
				(
					f,
				): f is LineFeature & {
					geometry: LineString | MultiLineString;
				} =>
					f.geometry.type === "LineString" ||
					f.geometry.type === "MultiLineString",
			);
		},
	});
}

export function useUpdateZoneMutation(http: Api<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (params: {
			missionId: string;
			id: string;
			zonePolygon: MultiPolygon;
			zoneType: BoundaryType.NoCoverageZone | BoundaryType.SpeedZone;
			speedLimit: number;
			name?: string;
		}) => {
			const body: BoundaryUpdate = {
				id: params.id,
				geometry: params.zonePolygon,
				type: params.zoneType,
				SpeedLimit: new Speed(params.speedLimit, "mph").convert("m/s"),
				name: params.name?.trim() || undefined,
			};
			return (await http.boundaries.boundariesUpdate(body)).data;
		},
		onSettled: async (_, __, params) => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["farms"],
				}),
				queryClient.invalidateQueries({
					queryKey: ["mission", params.missionId, "boundaries"],
				}),
			]);
		},
	});
}
